
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Trash, DollarSign, Clock } from "lucide-react";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { PricingComponent } from "@/types/Project";
import { Tables } from "@/integrations/supabase/types";
import { Json } from "@/integrations/supabase/types";

interface PricingComponentFormProps {
  pricingComponents: Tables<"projects">["pricing_components"];
  setPricingComponents: (components: Tables<"projects">["pricing_components"]) => void;
}

export function PricingComponentForm({ 
  pricingComponents, 
  setPricingComponents 
}: PricingComponentFormProps) {
  // Parse the pricing components if they're stored as a string
  const [parsedComponents, setParsedComponents] = useState<PricingComponent[]>(() => {
    if (!pricingComponents) return [];
    
    try {
      // If it's already an array, use it directly
      if (Array.isArray(pricingComponents)) {
        return pricingComponents as unknown as PricingComponent[];
      }
      
      // If it's a string, parse it
      if (typeof pricingComponents === 'string') {
        return JSON.parse(pricingComponents);
      }
      
      return [];
    } catch (error) {
      console.error("Error parsing pricing components:", error);
      return [];
    }
  });

  const addPricingComponent = () => {
    const newComponent: PricingComponent = {
      id: crypto.randomUUID(),
      name: "",
      pricingType: "hourly",
      amount: 0
    };
    
    const updatedComponents = [...parsedComponents, newComponent];
    setParsedComponents(updatedComponents);
    setPricingComponents(updatedComponents as unknown as Json);
  };

  const updatePricingComponent = (id: string, field: keyof PricingComponent, value: any) => {
    const updatedComponents = parsedComponents.map(component => 
      component.id === id ? { ...component, [field]: value } : component
    );
    setParsedComponents(updatedComponents);
    setPricingComponents(updatedComponents as unknown as Json);
  };

  const removePricingComponent = (id: string) => {
    const updatedComponents = parsedComponents.filter(component => component.id !== id);
    setParsedComponents(updatedComponents);
    setPricingComponents(updatedComponents as unknown as Json);
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button 
          type="button" 
          variant="outline" 
          size="sm" 
          onClick={addPricingComponent}
          className="flex items-center gap-1"
        >
          <Plus className="w-4 h-4" />
          Add Component
        </Button>
      </div>
      
      {parsedComponents.length > 0 ? (
        <div className="space-y-3">
          {parsedComponents.map((component) => (
            <Card key={component.id} className="relative">
              <CardContent className="pt-4 pb-2">
                <div className="flex items-start gap-2 mb-2">
                  <div className="flex-1">
                    <label className="text-xs font-medium mb-1 block">Component Name</label>
                    <Input
                      value={component.name}
                      onChange={(e) => updatePricingComponent(component.id, 'name', e.target.value)}
                      placeholder="e.g., Design, Development, etc."
                    />
                  </div>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="icon"
                    className="h-9 w-9 text-destructive hover:text-destructive hover:bg-destructive/10 -mt-1"
                    onClick={() => removePricingComponent(component.id)}
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-medium mb-1 block">Pricing Type</label>
                    <Select
                      value={component.pricingType}
                      onValueChange={(value) => updatePricingComponent(component.id, 'pricingType', value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select pricing type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hourly">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            <span>Hourly Rate</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="fixed">
                          <div className="flex items-center gap-1">
                            <DollarSign className="h-3 w-3" />
                            <span>Fixed Price</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium mb-1 block">
                      {component.pricingType === 'hourly' ? 'Hourly Rate ($)' : 'Fixed Price ($)'}
                    </label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={component.amount.toString()}
                      onChange={(e) => updatePricingComponent(component.id, 'amount', parseFloat(e.target.value))}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-6 text-muted-foreground border border-dashed rounded-md">
          No pricing components added yet. Click "Add Component" to get started.
        </div>
      )}
    </div>
  );
}
